import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { PaginatedList, Query, QueryParams } from '@services/core-services/models';
import {
  CustomerAddress,
  CustomerDetail,
  Customers,
  NotificationPreferences,
} from '@services/customer-services/models';
import { CustomerId } from './models/customer-id';
import { CustomerListResponse } from './models/customer.response';
import { CustomerDetailResponse } from './models/customer-detail.response';
import { KycDocument } from './models/kyc';
import { KycListResponse } from './models/kyc.response';
import { CreateBanking } from './models/create-bank';
import { BankingResponse, BusinessBanking } from './models/banking';
import { BankId } from './models/bank-id';
import { CreateAddress } from './models/create-address-request';
import { NotificationPreference } from './models/update-notification-preferences';
import { AddressId } from './models/address-id';
import { CreateDocument } from './constants/create-document';
import { updateCustomer } from './models/update-customer';
import { Product } from '@services/customer-services/models/customer-products';
import { CustomeProductResponse } from '@services/customer-services/models/customer-products.response';
import { AbandonedCartsResponse } from '@services/customer-services/models/abandoned-cart.response';
import { AbandonedCart } from '@services/customer-services/models/abandoned-carts';
import { KycDetail } from './models/kyc-detail';
import { DocumentId } from './models/document-id';
import { KycDetailResponse } from './models/kyc-detail.response';
import { UpdateAddress } from './models/update-address';
import { UpdateDocumentDetails } from './models/update-kyc-detail';
import { CustomerActivity } from './models/activities';
import { CustomerActivityResponse } from './models/activities-response';
import { CustomerAttribute } from './models/attributes';
import { CustomerAttributeResponse } from './models/attribute-response';
import { AttributeUnion } from './models/create-attribute-request';
import {
  AttributeId,
  CustomerAttributeListResponse,
  CustomerAttributes,
  CreateCustomerAttributeResponse,
} from '@services/attributes-service/models';
import { AttributeListQuery } from '@services/attributes-service/models';
import { CachingApiService } from '@services/core-services/api-cache.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  customerUrl = 'customers';
  orderUrl = 'orders';

  constructor(private readonly cacheApi: CachingApiService) {}

  getAllCustomerData(query: QueryParams): Observable<CustomerListResponse> {
    return this.cacheApi
      .getWithCache<CustomerListResponse>(this.customerUrl, query)
      .pipe(map((response: any) => response as CustomerListResponse));
  }

  getAllCustomers(query?: Query): Observable<PaginatedList<Customers[]>> {
    return this.cacheApi.getWithCache<CustomerListResponse>(this.customerUrl, query).pipe(
      map(({ content }: CustomerListResponse) => ({
        list: content.customers,
        pagination: content.pagination,
      }))
    );
  }

  deleteCustomer(customerId: CustomerId) {
    return this.cacheApi.delete(`${this.customerUrl}/${customerId}`);
  }

  getGlobalCustomerAttributes(
    query?: AttributeListQuery
  ): Observable<PaginatedList<CustomerAttributes[]>> {
    const url = `${this.customerUrl}/attributes`;
    return this.cacheApi.getWithCache<CustomerAttributeListResponse>(url, query).pipe(
      map(({ content }: CustomerAttributeListResponse) => ({
        list: content.attributes,
        pagination: content.pagination,
      }))
    );
  }

  createGlobalAttribute(attribute: Partial<CustomerAttributes>): Observable<CustomerAttributes> {
    const url = `${this.customerUrl}/attributes`;
    return this.cacheApi
      .post<Partial<CustomerAttributes>, CreateCustomerAttributeResponse>(url, attribute)
      .pipe(
        map((response: CreateCustomerAttributeResponse) => {
          return response.content.attribute;
        })
      );
  }

  updateGlobalAttribute(
    id: string,
    attribute: Partial<CustomerAttributes>
  ): Observable<CustomerAttributes> {
    const url = `${this.customerUrl}/attributes/${id}`;
    return this.cacheApi
      .put<Partial<CustomerAttributes>, CreateCustomerAttributeResponse>(url, attribute)
      .pipe(
        map((response: CreateCustomerAttributeResponse) => {
          return response.content.attribute;
        })
      );
  }

  // getGlobalAttributeDetail(customerId: CustomerId): Observable<CustomerDetail> {
  //   const urlId = `${this.customerUrl}/${customerId}`;
  //   return this.api.get<CustomerDetailResponse>(urlId).pipe(
  //     map((response: CustomerDetailResponse) => {
  //       return response.content.customer;
  //     })
  //   );
  // }

  getGlobalAttributeDetail(id: AttributeId): Observable<CustomerAttributes> {
    const url = `${this.customerUrl}/attributes/${id}`;
    return this.cacheApi.getWithCache<CreateCustomerAttributeResponse>(url).pipe(
      map((response: CreateCustomerAttributeResponse) => {
        return response.content.attribute;
      })
    );
  }

  getActivity(
    customerId: CustomerId,
    query?: Query
  ): Observable<PaginatedList<CustomerActivity[]>> {
    const url = `${this.customerUrl}/${customerId}/activities`;
    return this.cacheApi.getWithCache<CustomerActivityResponse>(url, query).pipe(
      map(({ content }: CustomerActivityResponse) => ({
        list: content.activities,
        pagination: content.pagination,
      }))
    );
  }

  getCustomerAttributes(customerId: CustomerId, query?: Query): Observable<CustomerAttribute[]> {
    query = query || {};
    query.limit = query.limit || 100 * 100;
    const url = `${this.customerUrl}/${customerId}/attributes`;
    return this.cacheApi.getWithCache<CustomerAttributeResponse>(url, query).pipe(
      map((response: CustomerAttributeResponse) => {
        return response.content.attributes;
      })
    );
  }

  getCustomerProducts(query?: Query): Observable<Product[]> {
    const url = `${this.orderUrl}/products-purchase-count`;
    return this.cacheApi.getWithCache<CustomeProductResponse>(url, query).pipe(
      map((response: CustomeProductResponse) => {
        return response.content.products;
      })
    );
  }

  getAbondandCarts(query?: Query): Observable<AbandonedCart[]> {
    const url = `${this.orderUrl}/abandoned-carts`;
    return this.cacheApi.getWithCache<AbandonedCartsResponse>(url, query).pipe(
      map((response: AbandonedCartsResponse) => {
        return response.content.abandoned_carts;
      })
    );
  }

  getCustomerDetail(customerId: CustomerId): Observable<CustomerDetail> {
    const urlId = `${this.customerUrl}/${customerId}`;
    return this.cacheApi.getWithCache<CustomerDetailResponse>(urlId).pipe(
      map((response: CustomerDetailResponse) => {
        return response.content.customer;
      })
    );
  }

  getCustomerKyc(customerId: CustomerId, query?: Query): Observable<PaginatedList<KycDocument[]>> {
    query = query || {};
    query.limit = query.limit || 10;
    const urlId = `${this.customerUrl}/${customerId}/documents`;
    return this.cacheApi.getWithCache<KycListResponse[]>(urlId, query).pipe(
      map((response: any) => {
        return {
          list: response.content.documents,
          pagination: response.content.pagination,
        };
      })
    );
  }

  getDocumentDetail(customerId: CustomerId, documentId: DocumentId): Observable<KycDetail> {
    const urlId = `${this.customerUrl}/${customerId}/documents/${documentId}`;
    return this.cacheApi.getWithCache<KycDetailResponse>(urlId).pipe(
      map((response: KycDetailResponse) => {
        return response.content.document;
      })
    );
  }

  updateDocumentDetail(
    customerId: CustomerId,
    documentId: DocumentId,
    documentData: Partial<UpdateDocumentDetails> | UpdateDocumentDetails,
    additionalData?: { [key: string]: any } // Optional additional data
  ): Observable<KycDetail> {
    const urlId = `${this.customerUrl}/${customerId}/documents/${documentId}`;
    const formData = new FormData();
    Object.keys(documentData).forEach(key => {
      formData.append(key, (documentData as any)[key]);
    });

    // Append additional data if provided
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }
    return this.cacheApi
      .put<FormData, KycDetailResponse>(urlId, formData) // Changed to post to use formData
      .pipe(
        map((response: KycDetailResponse) => {
          console.log(response);
          return response.content.document;
        })
      );
  }

  createDocuments(
    customerId: CustomerId,
    createDocument: CreateDocument,
    additionalData?: { [key: string]: any } // Optional additional data
  ): Observable<KycDocument[]> {
    const urlId = `${this.customerUrl}/${customerId}/documents`;
    const formData = new FormData();
    Object.keys(createDocument).forEach(key => {
      formData.append(key, (createDocument as any)[key]);
    });
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    return this.cacheApi.post<FormData, KycListResponse>(urlId, formData).pipe(
      map((response: KycListResponse) => {
        return response.content.documents;
      })
    );
  }

  createCustomerAddress(
    customerId: CustomerId,
    addAddress: CreateAddress
  ): Observable<CustomerAddress[]> {
    const urlId = `${this.customerUrl}/${customerId}/addresses`;
    return this.cacheApi.post<CreateAddress, CustomerAddress[]>(urlId, addAddress).pipe(
      map((response: CustomerAddress[]) => {
        return response;
      })
    );
  }

  deleteAddress(customerId: CustomerId, addressId: AddressId) {
    const urlId = `${this.customerUrl}/${customerId}/addresses/${addressId}`;
    return this.cacheApi.delete(urlId);
  }

  updateCustomer(
    customerId: CustomerId,
    customer: Partial<updateCustomer> | updateCustomer
  ): Observable<CustomerDetail> {
    const urlId = `${this.customerUrl}/${customerId}`;
    return this.cacheApi
      .put<Partial<updateCustomer> | updateCustomer, CustomerDetailResponse>(urlId, customer)
      .pipe(
        map((response: CustomerDetailResponse) => {
          return response.content.customer;
        })
      );
  }

  createCustomers(customer: Partial<CustomerDetail>): Observable<CustomerDetail> {
    const url = `${this.customerUrl}`;
    return this.cacheApi.post<Partial<CustomerDetail>, CustomerDetailResponse>(url, customer).pipe(
      map((response: CustomerDetailResponse) => {
        return response.content.customer;
      })
    );
  }

  updateCustomerAddress(
    customerId: CustomerId,
    addressId: AddressId,
    addresses: Partial<UpdateAddress> | UpdateAddress
  ): Observable<CustomerAddress> {
    const urlId = `${this.customerUrl}/${customerId}/addresses/${addressId}`;
    return this.cacheApi
      .put<Partial<UpdateAddress> | UpdateAddress, CustomerAddress>(urlId, addresses)
      .pipe(
        map((response: CustomerAddress) => {
          return response;
        })
      );
  }

  updateBankAccount(
    customerId: CustomerId,
    bankId: BankId,
    bank_account: Partial<CreateBanking> | CreateBanking
  ): Observable<BankingResponse> {
    const urlId = `${this.customerUrl}/${customerId}/banking/${bankId}`;
    return this.cacheApi
      .put<Partial<CreateBanking> | CreateBanking, BankingResponse>(urlId, bank_account)
      .pipe(
        map((response: BankingResponse) => {
          return response;
        })
      );
  }

  createBankAccount(customerId: CustomerId, addBank: CreateBanking): Observable<BusinessBanking> {
    const urlId = `${this.customerUrl}/${customerId}/banking`;
    return this.cacheApi.post<CreateBanking, BusinessBanking>(urlId, addBank).pipe(
      map((response: BusinessBanking) => {
        return response;
      })
    );
  }
  deleteBank(customerId: CustomerId, bankId: BankId) {
    const urlId = `${this.customerUrl}/${customerId}/banking/${bankId}`;
    return this.cacheApi.delete(urlId);
  }

  updateNotificationPreferences(
    customerId: CustomerId,
    notification_preferences: Partial<NotificationPreference> | NotificationPreference
  ): Observable<NotificationPreferences> {
    const urlId = `${this.customerUrl}/${customerId}/notification-preferences`;
    return this.cacheApi
      .put<
        Partial<NotificationPreference> | NotificationPreference,
        NotificationPreferences
      >(urlId, notification_preferences)
      .pipe(
        map((response: NotificationPreferences) => {
          return response;
        })
      );
  }

  createCustomerAttribute(
    customerId: CustomerId,
    attributes: AttributeUnion[]
  ): Observable<CustomerAttributeResponse> {
    const url = `${this.customerUrl}/${customerId}/attributes`;
    const payload = attributes;

    return this.cacheApi.post<any, CustomerAttributeResponse>(url, payload).pipe(
      map((response: CustomerAttributeResponse) => {
        return response;
      })
    );
  }

  updateCustomerAttribute(
    customerId: CustomerId,
    attributeId: AttributeId,
    attributes: Partial<AttributeUnion[]> | AttributeUnion[]
  ): Observable<CustomerAttribute[]> {
    const url = `${this.customerUrl}/${customerId}/attributes/${attributeId}`;
    return this.cacheApi
      .put<Partial<AttributeUnion[]> | AttributeUnion[], CustomerAttributeResponse>(url, attributes)
      .pipe(
        map((response: any) => {
          return response; // Extract updated attributes from the response
        })
      );
  }

  deleteCustomerAttribute(customerId: CustomerId, attributeId: AttributeId) {
    const url = `${this.customerUrl}/${customerId}/attributes/${attributeId}`;
    return this.cacheApi.delete(url);
  }

  deleteGlobalAttribute(attributeId: AttributeId) {
    const url = `${this.customerUrl}/attributes/${attributeId}`;
    return this.cacheApi.delete(url);
  }
}
